import React from 'react';
import { Plus, Trash2, Edit2, Save, X, GripVertical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const DESTINATIONS = ['ANMAR', 'GAIKER', 'IVAMI', 'REGULATORY'];

/**
 * Componente de item de estado con soporte de drag & drop
 */
const SortableEstadoItem = ({
  estado,
  editingEstado,
  onEditingEstadoChange,
  onEdit,
  onUpdate,
  onDelete,
  onToggleDestination,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: estado.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isEditing = editingEstado && editingEstado.id === estado.id;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-3 bg-gray-50 rounded-lg border-2 ${
        isDragging ? 'border-blue-500 shadow-lg' : 'border-transparent'
      }`}
      data-testid={`estado-${estado.id}`}
    >
      {isEditing ? (
        // Edit Mode
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              value={editingEstado.name}
              onChange={(e) =>
                onEditingEstadoChange({ ...editingEstado, name: e.target.value })
              }
              placeholder="Nombre del estado"
              data-testid={`edit-estado-name-${estado.id}`}
            />
            <Input
              type="color"
              value={editingEstado.color}
              onChange={(e) =>
                onEditingEstadoChange({ ...editingEstado, color: e.target.value })
              }
              className="w-24"
              data-testid={`edit-estado-color-${estado.id}`}
            />
          </div>
          <div>
            <Label className="text-sm mb-2 block">Destinos Permitidos:</Label>
            <div className="flex flex-wrap gap-2">
              {DESTINATIONS.map((dest) => (
                <div key={dest} className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-estado-${estado.id}-${dest}`}
                    checked={editingEstado.destinations.includes(dest)}
                    onCheckedChange={() => onToggleDestination(dest)}
                  />
                  <label htmlFor={`edit-estado-${estado.id}-${dest}`} className="text-sm cursor-pointer">
                    {dest}
                  </label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onUpdate}
              size="sm"
              className="flex-1"
              data-testid={`save-estado-${estado.id}`}
            >
              <Save size={16} className="mr-1" />
              Guardar
            </Button>
            <Button
              variant="outline"
              onClick={() => onEditingEstadoChange(null)}
              size="sm"
              data-testid={`cancel-edit-estado-${estado.id}`}
            >
              <X size={16} />
            </Button>
          </div>
        </div>
      ) : (
        // View Mode with Drag Handle
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
            >
              <GripVertical size={20} className="text-gray-400" />
            </div>
            <div
              className="w-6 h-6 rounded-full border-2 border-gray-300"
              style={{ backgroundColor: estado.color }}
            />
            <div className="flex-1">
              <span className="font-medium">{estado.name}</span>
              <div className="flex gap-1 mt-1">
                {(estado.destinations || []).map((dest) => (
                  <Badge key={dest} variant="secondary" className="text-xs">
                    {dest}
                  </Badge>
                ))}
                {(!estado.destinations || estado.destinations.length === 0) && (
                  <span className="text-xs text-gray-500 italic">Sin destinos</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onEdit(estado)}
              data-testid={`edit-estado-${estado.id}`}
            >
              <Edit2 size={16} />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(estado.id, estado.name)}
              data-testid={`delete-estado-${estado.id}`}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Pestaña de gestión de estados con drag & drop para reordenar
 */
const EstadosTab = ({
  estados,
  newEstado,
  onNewEstadoChange,
  editingEstado,
  onEditingEstadoChange,
  onAddEstado,
  onEditEstado,
  onUpdateEstado,
  onDeleteEstado,
  onToggleDestination,
  onDragEnd,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Gestión de Estado</CardTitle>
        <p className="text-sm text-gray-600 mt-2">
          Arrastra los estados para reordenarlos. El orden se reflejará en Introducción de Datos.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Nuevo Estado */}
        <div className="border p-4 rounded-lg bg-blue-50">
          <h3 className="font-semibold mb-3">Nuevo Estado</h3>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                placeholder="Nombre del estado"
                value={newEstado.name}
                onChange={(e) => onNewEstadoChange({ ...newEstado, name: e.target.value })}
                data-testid="new-estado-name"
                className="flex-1"
              />
              <Input
                type="color"
                value={newEstado.color}
                onChange={(e) => onNewEstadoChange({ ...newEstado, color: e.target.value })}
                className="w-24"
                data-testid="new-estado-color"
              />
            </div>
            <div>
              <Label className="text-sm mb-2 block">Destinos Permitidos:</Label>
              <div className="flex flex-wrap gap-2">
                {DESTINATIONS.map((dest) => (
                  <div key={dest} className="flex items-center space-x-2">
                    <Checkbox
                      id={`new-estado-${dest}`}
                      checked={newEstado.destinations.includes(dest)}
                      onCheckedChange={() => onToggleDestination(dest)}
                    />
                    <label htmlFor={`new-estado-${dest}`} className="text-sm cursor-pointer">
                      {dest}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            <Button onClick={onAddEstado} className="w-full" data-testid="add-estado-btn">
              <Plus size={18} className="mr-1" />
              Añadir Estado
            </Button>
          </div>
        </div>

        {/* Lista de Estados con Drag & Drop */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={estados.map((e) => e.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {estados.map((estado) => (
                <SortableEstadoItem
                  key={estado.id}
                  estado={estado}
                  editingEstado={editingEstado}
                  onEditingEstadoChange={onEditingEstadoChange}
                  onEdit={onEditEstado}
                  onUpdate={onUpdateEstado}
                  onDelete={onDeleteEstado}
                  onToggleDestination={onToggleDestination}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </CardContent>
    </Card>
  );
};

export default EstadosTab;
